import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Calculator, Loader2, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { reconcileStock } from '@/app/services/inventoryService';
import { Godown, ItemStockSummaryResponse } from '@/app/types/inventory';
import { Item } from '@/app/types/item';

const stockReconciliationSchema = z.object({
  godown_id: z.number({
    required_error: 'Please select a godown',
  }),
  physical_quantity: z
    .string()
    .min(1, 'Physical quantity is required')
    .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
      message: 'Physical quantity must be a non-negative number',
    }),
  notes: z.string().optional(),
});

type StockReconciliationFormData = z.infer<typeof stockReconciliationSchema>;

interface StockReconciliationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: Item;
  godowns: Godown[];
  stockSummary?: ItemStockSummaryResponse;
}

export function StockReconciliationDialog({
  open,
  onOpenChange,
  item,
  godowns,
  stockSummary,
}: StockReconciliationDialogProps) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<StockReconciliationFormData>({
    resolver: zodResolver(stockReconciliationSchema),
    defaultValues: {
      physical_quantity: '',
      notes: '',
    },
  });

  const reconcileMutation = useMutation({
    mutationFn: (data: StockReconciliationFormData) =>
      reconcileStock(item.id, {
        godown_id: data.godown_id,
        physical_quantity: data.physical_quantity,
        notes: data.notes,
      }),
    onSuccess: (result) => {
      if (result) {
        toast.success('Stock reconciled successfully');
      } else {
        toast.info('No adjustment needed - stock matches physical count');
      }
      queryClient.invalidateQueries({ queryKey: ['item-stock-summary', item.id] });
      queryClient.invalidateQueries({ queryKey: ['current-stock', item.id] });
      form.reset();
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to reconcile stock');
    },
  });

  const handleSubmit = async (data: StockReconciliationFormData) => {
    setIsSubmitting(true);
    try {
      await reconcileMutation.mutateAsync(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedGodownId = form.watch('godown_id');
  const physicalQuantityStr = form.watch('physical_quantity');
  const physicalQuantity = physicalQuantityStr ? parseFloat(physicalQuantityStr) : 0;

  const selectedGodownStock = stockSummary?.stock_by_godown.find(
    (stock) => stock.godown_id === selectedGodownId
  );
  const systemQuantity = selectedGodownStock
    ? parseFloat(selectedGodownStock.quantity)
    : 0;

  const difference = selectedGodownId && physicalQuantityStr
    ? physicalQuantity - systemQuantity
    : 0;

  const activeGodowns = godowns.filter((g) => g.is_active);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Stock Reconciliation</DialogTitle>
          <DialogDescription>
            Adjust system stock for {item.name} to match physical count
          </DialogDescription>
        </DialogHeader>

        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            This action will adjust the system stock to match your physical count. 
            Only authorized personnel should perform stock reconciliation.
          </AlertDescription>
        </Alert>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="godown_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Godown</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    value={field.value?.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select godown to reconcile" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {activeGodowns.map((godown) => {
                        const stock = stockSummary?.stock_by_godown.find(
                          (s) => s.godown_id === godown.id
                        );
                        const qty = stock ? parseFloat(stock.quantity) : 0;
                        return (
                          <SelectItem key={godown.id} value={godown.id.toString()}>
                            {godown.name} (System: {qty.toFixed(2)} {item.unit?.short_name})
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedGodownId && (
              <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">System Quantity:</span>
                  <span className="font-medium">
                    {systemQuantity.toFixed(2)} {item.unit?.short_name}
                  </span>
                </div>
              </div>
            )}

            <FormField
              control={form.control}
              name="physical_quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Physical Count</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Calculator className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        className="pl-10"
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormDescription>
                    Enter the actual quantity counted in the godown
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedGodownId && physicalQuantityStr && (
              <div className="p-4 border rounded-lg space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Adjustment Required:</span>
                  {difference !== 0 ? (
                    <div className="flex items-center gap-2">
                      {difference > 0 ? (
                        <Badge variant="default" className="gap-1">
                          <TrendingUp className="h-3 w-3" />
                          +{difference.toFixed(2)} {item.unit?.short_name}
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="gap-1">
                          <TrendingDown className="h-3 w-3" />
                          {difference.toFixed(2)} {item.unit?.short_name}
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <Badge variant="secondary">No adjustment needed</Badge>
                  )}
                </div>
                {difference !== 0 && (
                  <p className="text-sm text-gray-600">
                    {difference > 0
                      ? 'Stock will be increased (adjustment in)'
                      : 'Stock will be decreased (adjustment out)'}
                  </p>
                )}
              </div>
            )}

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Explain the reason for adjustment (e.g., damaged goods, counting error)..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {difference !== 0 && 'Please provide a reason for the stock difference'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !selectedGodownId || !physicalQuantityStr}
                variant={difference < 0 ? 'destructive' : 'default'}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Reconciling...
                  </>
                ) : (
                  'Reconcile Stock'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}